import { Trans, useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import {
	Button,
	Emoji,
	Grid,
	PopoverWithTrigger,
	Spacing,
} from '@/design-system'
import { useTracking } from '@/hooks/useTracking'
import { useUrl } from '@/hooks/useUrl'

import { type ConseillersEntreprisesVariant } from '../ConseillersEntreprises/BoutonConseillersEntreprises'
import { ConseillersEntreprisesButton } from '../ConseillersEntreprisesButton'
import { ShareSimulationPopup } from './ShareSimulationPopup'

export default function ShareOrSaveSimulationBanner({
	share,
	print,
	conseillersEntreprisesVariant,
}: {
	share?: boolean
	print?: boolean
	conseillersEntreprisesVariant?: ConseillersEntreprisesVariant
}) {
	const { t } = useTranslation()
	const { trackClick } = useTracking()
	const shareAPIAvailable = !!window?.navigator?.share
	const url = useUrl()
	const startSharing = async () => {
		if (shareAPIAvailable) {
			try {
				await window.navigator.share({
					title: document.title,
					text: t(
						'shareSimulation.navigatorShare',
						'Ma simulation Mon Entreprise'
					),
					url,
				})
			} catch (e) {
				if (
					e instanceof Error &&
					(e.toString().includes('AbortError') || /permission/i.test(e.message))
				) {
					return
				}
				// eslint-disable-next-line no-console
				console.error(e)
			}
		}
	}

	return (
		<>
			<Spacing lg />
			<Grid
				as={LinksList}
				container
				className=" print-hidden"
				spacing={4}
				style={{
					justifyContent: 'center',
				}}
			>
				{share && (
					<Grid as="li" item xs={12} sm="auto">
						<PopoverWithTrigger
							title={t('shareSimulation.modal.title', 'Votre lien de partage')}
							trigger={(buttonProps) => (
								<Button
									{...buttonProps}
									light
									size="XS"
									onPress={(e) => {
										trackClick({
											action: 'démarré',
											feature: 'feature:partage',
										})
										startSharing().catch(
											// eslint-disable-next-line no-console
											(err) => console.error(err)
										)
										buttonProps?.onPress?.(e)
									}}
									aria-haspopup="dialog"
								>
									<Emoji emoji="🔗" />
									<ButtonLabel>
										<Trans i18nKey="shareSimulation.banner">
											Générer un lien de partage
										</Trans>
									</ButtonLabel>
								</Button>
							)}
							small
						>
							<ShareSimulationPopup url={url} />
						</PopoverWithTrigger>
					</Grid>
				)}

				{print && typeof window.print === 'function' && (
					<Grid as="li" item xs={12} sm="auto">
						<Button
							light
							size="XS"
							onPress={() => window.print()}
							aria-haspopup="dialog"
						>
							<Emoji emoji="🖨" />
							<ButtonLabel>
								<Trans i18nKey="ExportSimulation.Banner">
									Imprimer ou sauvegarder en PDF
								</Trans>
							</ButtonLabel>
						</Button>
					</Grid>
				)}

				{conseillersEntreprisesVariant && (
					<Grid as="li" item xs={12} sm="auto">
						<ConseillersEntreprisesButton
							variant={conseillersEntreprisesVariant}
						/>
					</Grid>
				)}
			</Grid>
		</>
	)
}

const ButtonLabel = styled.span`
	margin-left: 1rem;
`

const LinksList = styled.ul`
	list-style: none;
`
